import React, { useMemo } from "react";
import { Drawer, Box, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { useCart } from "@ecommerce/store";
import { trackMetric } from "@ecommerce/analytics";

import { LG } from "../../styles/liquidGlass";
import { Icon } from "../liquid/LiquidPrimitives";
import { DrawerHeader } from "./ShoppingCartDrawerHeader";
import { EmptyCart } from "./ShoppingCartEmptyCart";
import { ShoppingCartItem } from "./ShoppingCartItem";
import { useTiendaInfo } from "../../contexts/TiendaInfoContext";
import { buildCartWhatsAppUrl } from "../../utils/whatsapp";

import { AppConfigFields } from "../../types/appConfigEntities";

const minimumPurchase = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseQuantity] || 1;

interface ShoppingCartDrawerProps {
    cartItems: any[];
    isOpen: boolean;
    onClose: () => void;
    cartCount: number;
}

const ShoppingCartDrawer: React.FC<ShoppingCartDrawerProps> = ({ cartItems, isOpen, onClose }) => {
    const navigate = useNavigate();
    const { updateQuantity, removeFromCart } = useCart();
    const tiendaInfo = useTiendaInfo();
    const bitEsCatalogo = tiendaInfo?.esCatalogo ?? false;

    const total = useMemo(
        () => cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
        [cartItems],
    );

    const handleUpdateQuantity = (
        idProduct: string,
        idProductVariant: string,
        newQuantity: number,
    ) => {
        updateQuantity(idProduct, idProductVariant, Math.max(minimumPurchase, newQuantity));
    };

    const handleRemoveItem = (idProduct: string, idProductVariant: string) => {
        removeFromCart(idProduct, idProductVariant);
    };

    const handleCheckout = () => {
        onClose();
        navigate("/checkout");
    };

    const handleConsultarWhatsApp = () => {
        const waItems = cartItems.map((item) => ({
            name: item.name ?? item.productName ?? "Producto",
            price: item.price,
            quantity: item.quantity,
            attributes: item.attributes,
        }));
        const url = buildCartWhatsAppUrl(waItems, tiendaInfo?.whatsappNumero);
        const productIds = new Set(
            cartItems
                .map((item) => item.idProduct)
                .filter((productId): productId is string => Boolean(productId)),
        );

        productIds.forEach((productId) => {
            trackMetric("PRODUCT_WHATSAPP", { entityId: productId });
        });
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const hasItems = cartItems.length > 0;
    const minimumAmount = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseAmount] ?? 0;
    const reachesMinimum = total >= minimumAmount;

    const blockReason: string | null = !hasItems
        ? "Tu carrito está vacío"
        : !reachesMinimum
          ? `El monto mínimo de compra es $${minimumAmount.toLocaleString("es-AR")}`
          : null;

    const canCheckout = blockReason === null;

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                className: "lg-strong",
                style: {
                    boxShadow:
                        "-30px 0 80px -20px rgba(60,40,20,0.25), inset 0 1px 0 rgba(255,255,255,0.85)",
                },
            }}
            ModalProps={{
                BackdropProps: {
                    sx: { backgroundColor: "rgba(20,16,12,0.18)", backdropFilter: "blur(2px)" },
                },
            }}
            sx={{
                "& .MuiDrawer-paper": {
                    width: { xs: "calc(100% - 16px)", md: 444 },
                    maxWidth: "calc(100% - 16px)",
                    top: "12px",
                    bottom: "12px",
                    right: "12px",
                    height: "auto",
                    boxSizing: "border-box",
                    color: LG.color.text,
                    borderRadius: "28px !important",
                    display: "flex",
                    flexDirection: "column",
                    px: { xs: 2, sm: 3.5 },
                    py: 3,
                    background: "var(--glass-bg-strong) !important",
                },
                zIndex: (t) => t.zIndex.drawer + 2000,
            }}
        >
            <DrawerHeader onClose={onClose} />

            <Box sx={{ flexGrow: 1, overflow: "auto", height: "100%", mb: 2 }}>
                {!hasItems ? (
                    <EmptyCart />
                ) : (
                    <Box sx={{ mb: 3 }}>
                        {cartItems.map((item, index) => (
                            <ShoppingCartItem
                                key={`${item.idProduct}-${item.idProductVariant}-${index}`}
                                item={item}
                                onUpdateQuantity={handleUpdateQuantity}
                                onRemoveItem={handleRemoveItem}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {hasItems && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "10px 4px 4px",
                            borderTop: "1px solid var(--divider)",
                            marginTop: 2,
                        }}
                    >
                        <span style={{ fontSize: 14, fontWeight: 600 }}>Total</span>
                        <span className="mono" style={{ fontSize: 16, fontWeight: 700 }}>
                            ${total.toLocaleString("es-AR")}
                        </span>
                    </div>

                    {bitEsCatalogo ? (
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={handleConsultarWhatsApp}
                            style={{
                                justifyContent: "center",
                                gap: 10,
                                width: "100%",
                                marginTop: 6,
                            }}
                        >
                            <Icon.whatsapp /> Consultar por WhatsApp
                        </button>
                    ) : (
                        <Tooltip
                            title={blockReason ?? ""}
                            disableHoverListener={canCheckout}
                            disableFocusListener={canCheckout}
                            disableTouchListener={canCheckout}
                            arrow
                            placement="top"
                        >
                            <span style={{ display: "block", marginTop: 6 }}>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    disabled={!canCheckout}
                                    onClick={handleCheckout}
                                    style={{
                                        justifyContent: "center",
                                        gap: 10,
                                        width: "100%",
                                        opacity: canCheckout ? 1 : 0.5,
                                        cursor: canCheckout ? "pointer" : "not-allowed",
                                        pointerEvents: canCheckout ? "auto" : "none",
                                    }}
                                >
                                    <Icon.bag /> Iniciar compra
                                </button>
                            </span>
                        </Tooltip>
                    )}

                    <button
                        type="button"
                        className="btn-ghost"
                        style={{ justifyContent: "center" }}
                        onClick={onClose}
                    >
                        Seguir comprando
                    </button>
                </div>
            )}
        </Drawer>
    );
};

export default ShoppingCartDrawer;
